import React, { useCallback, useEffect, useState, useMemo } from "react"
import { Loader } from "common/Loader"
import UserService from "services/user.service"
import DatePicker from "react-datepicker"
import { DATE } from "./trainings/WeeklyStats"
import { formatDateY } from "utils/funcs"
import ReactApexChart from "react-apexcharts"

const service = new UserService()

export const ScreenerStats = () => {
  const [data, setData] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [startDate, setStartDate] = useState<any>(null)
  const [endDate, setEndDate] = useState<any>(null)
  const [companies, setCompanies] = React.useState<any[]>([])
  const [date, setDate] = useState("")
  const [users, setUsers] = React.useState<any[]>([])
  const [ads, setAds] = React.useState<any[]>([])
  const [showCustom, setShowCustom] = useState(false)
  const [filters, setFilters] = useState({
    userId: "",
    date: "",
    adId: "",
    companyId: "",
  })

  const headers = [
    "Name",
    "Call Attemps",
    "Screened",
    "Screen Rate",
    "Screen Accepted",
    "Screen Rejected",
    "Interview Accepted",
    "Interview Rejected",
  ]

  const cellClasses =
    "py-3 px-4 text-sm border-r border-gray-200 transition-colors duration-150"

  const fetchData = useCallback(() => {
    setLoading(true)

    Promise.all([service.getCompanies(), service.getUsers()])
      .then(([resp1, resp2]) => {
        setCompanies(resp1.data.data)
        setUsers(resp2.data.data)
        setLoading(false)
      })
      .catch((error) => {
        console.log(error)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    service
      .dealersMediaAds({ companyId: filters.companyId, pageSize: 1000 })
      .then((resp) => {
        setAds(resp.data.data)
      })
      .catch((error) => console.log(error))
  }, [filters.companyId])

  useEffect(() => {
    setLoading(true)

    service
      .screenerStats({ ...filters, date })
      .then((resp) => {
        setData(resp.data.data)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })
  }, [filters, date])

  const handleChangeDates = (dates) => {
    const [start, end] = dates

    setStartDate(start)
    setEndDate(end)

    if ((!start && !end) || end) {
      setDate(`${formatDateY(start)},${formatDateY(end)}`)
    }
  }

  const hasFilters = Object.values(filters).some((val) => val)

  // Prepare chart data per user
  const userNames = useMemo(() => {
    return data.map((user) => user.fullname || user.email || "Unknown")
  }, [data])

  const chart1Options = {
    chart: {
      type: "bar" as const,
      height: 350,
      toolbar: {
        show: false,
      },
      foreColor: "#646A71",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: userNames,
      labels: {
        style: {
          fontSize: "12px",
        },
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      title: {
        text: "Count",
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top" as const,
    },
    colors: ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"],
    grid: {
      borderColor: "#F4F5F6",
    },
  }

  const chart1Series = [
    {
      name: "Call Attempts",
      data: data.map((user) => user.callAttempt || 0),
    },
    {
      name: "Screened",
      data: data.map((user) => user.screenTotalCount || 0),
    },
    {
      name: "Screen Accepted",
      data: data.map((user) => user.screenAcceptedCount || 0),
    },
    {
      name: "Interview Accepted",
      data: data.map((user) => user.interviewAccepted || 0),
    },
  ]

  const chart2Options = {
    chart: {
      type: "bar" as const,
      height: 350,
      toolbar: {
        show: false,
      },
      foreColor: "#646A71",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: userNames,
      labels: {
        style: {
          fontSize: "12px",
        },
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      title: {
        text: "Percentage",
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top" as const,
    },
    colors: ["#EF4444", "#10B981", "#3B82F6"],
    grid: {
      borderColor: "#F4F5F6",
    },
  }

  const chart2Series = [
    {
      name: "Screen Rate",
      data: data.map((user) => user.screenRate || 0),
    },
    {
      name: "Screen Accepted %",
      data: data.map((user) => user.screenAcceptedPct || 0),
    },
    {
      name: "Interview Rate %",
      data: data.map((user) => user.interviewAcceptedPct || 0),
    },
  ]

  return (
    <div className="min-h-screen">
      <h1 className="page-title">
        Screener Stats
      </h1>
      <p className="mb-3 text-sm text-gray-500">
        View perfromance metrics for screeners
      </p>

      <div className="card p-4">
        <h3 className="flex items-center mb-3 text-lg font-semibold text-gray-800">
          Filter Report
        </h3>

        <div className="flex gap-2">
          <div className="flex flex-col flex-1 flex-shrink-0 w-full gap-1 min-w-[150px]">
            <label className="text-sm font-medium text-gray-600">Date</label>
            <select
              className="w-full px-3 py-2 text-sm"
              value={filters.date}
              onChange={(e) => {
                if (e.target.value === "CUSTOM") {
                  setShowCustom(true)
                } else {
                  setDate(e.target.value)
                  setFilters({
                    ...filters,
                    date: e.target.value,
                  })
                }
              }}
            >
              <option value="">All</option>
              {DATE.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col flex-1 flex-shrink-0 w-full gap-1 min-w-[150px]">
            <label className="text-sm font-medium text-gray-600">
              Companies
            </label>

            <select
              name="companyId"
              value={filters.companyId}
              className="w-full px-3 py-2 text-sm"
              onChange={(e) =>
                setFilters({ ...filters, companyId: e.target.value })
              }
            >
              <option>Select company</option>
              {companies.map((item) => (
                <option key={item.companyId} value={item.companyId}>{item.title}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col flex-1 gap-1 min-w-[150px]">
            <label className="text-sm font-medium text-gray-600">
              Campaign Name
            </label>
            <select
              name="adId"
              value={filters.adId}
              className="w-full px-3 py-2 text-sm"
              onChange={(e) => setFilters({ ...filters, adId: e.target.value })}
            >
              <option value="">All</option>
              {ads.map((item: any) => (
                <option value={item._id}>{item.campaignName}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col flex-1 gap-1 min-w-[150px]">
            <label className="text-sm font-medium text-gray-600">Users</label>
            <select
              name="userId"
              value={filters.userId}
              className="w-full px-3 py-2 text-sm"
              onChange={(e) =>
                setFilters({ ...filters, userId: e.target.value })
              }
            >
              <option value="">All</option>
              {users.map((item: any) => (
                <option value={item._id}>{item.fullname}</option>
              ))}
            </select>
          </div>
          {(hasFilters || date) && (
            <button
              type="button"
              onClick={() => {
                setFilters({ userId: "", date: "", adId: "", companyId: "" })
                setShowCustom(false)
                setStartDate(null)
                setDate('')
                setEndDate(null)
              }}
              className="bg-primary text-white  text-sm rounded-md px-3 place-self-end py-2.5"
            >
              Clear Filters
            </button>
          )}
        </div>
        {showCustom && (
          <DatePicker
            selected={startDate}
            placeholderText="Select range"
            onChange={handleChangeDates}
            startDate={startDate}
            endDate={endDate}
            className="z-100 py-2 mt-2 "
            customInput={
              <input
                placeholder="Select date"
                className="rounded-md border w-72 border-gray-outline outline-none focus-visible:shadow-none placeholder:text-sm focus:ring-0 focus:border-gray-200 py-2 text-sm text-zinc-600 pl-4 pr-4 disabled:bg-zinc-50 disabled:text-zinc-500 disabled:cursor-not-allowed text-dark-8 font-TTHoves;"
              />
            }
            selectsRange
            selectsDisabledDaysInRange
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 my-4 md:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-2.5 text-center text-lg font-semibold text-gray-800">
            Volume Metrics by Screener
          </h3>
          {data.length > 0 ? (
            <ReactApexChart
              options={chart1Options}
              series={chart1Series}
              type="bar"
              height={350}
            />
          ) : (
            <div className="flex items-center justify-center h-[350px] text-gray-500">
              No data available
            </div>
          )}
        </div>

        <div className="card p-4">
          <h3 className="mb-2.5 text-center text-lg font-semibold text-gray-800">
            Rate Metrics by Screener (%)
          </h3>
          {data.length > 0 ? (
            <ReactApexChart
              options={chart2Options}
              series={chart2Series}
              type="bar"
              height={350}
            />
          ) : (
            <div className="flex items-center justify-center h-[350px] text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-left bg-white border-collapse table-auto">
          <thead className="bg-gray-100">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={header}
                  className={`py-3 px-4 text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-300 border-r `}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 && !loading ? (
              data.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className={`${cellClasses} font-medium text-gray-800 `}>
                    {item?.fullname}
                  </td>
                  <td className={`${cellClasses} text-gray-600`}>
                    {item?.callAttempt}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.screenTotalCount}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.screenRate}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.screenAcceptedCount}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.screenRejectedCount}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.interviewAccepted}
                  </td>
                  <td className={`${cellClasses} text-gray-600 text-right`}>
                    {item?.interviewRejected}
                  </td>
                </tr>
              ))
            ) : loading ? (
              <tr className="h-36">
                <td colSpan={headers.length}>
                  <Loader />
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className="p-8 text-center text-gray-500"
                >
                  No data available for the selected date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
